import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { LucideIcon, ArrowUpRight, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SourceBadge, DataSource } from './SourceBadge';

interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  source?: DataSource;
  trend?: string;
  trendDirection?: 'UP' | 'DOWN' | 'NEUTRAL';
  icon: LucideIcon;
  variant?: 'default' | 'copper' | 'primary' | 'heroEmerald';
  className?: string;
}

// Enterprise Micro-Sparkline Component
const MicroSparkline: React.FC<{ isUp: boolean; isHero?: boolean; colorHex?: string }> = ({
  isUp,
  isHero = false,
  colorHex = '#3d5ad6',
}) => {
  const pathUp = 'M 0 28 C 15 25, 30 12, 45 18 C 60 24, 75 8, 90 4 L 105 10 L 120 2';
  const areaPathUp = 'M 0 28 C 15 25, 30 12, 45 18 C 60 24, 75 8, 90 4 L 105 10 L 120 2 L 120 36 L 0 36 Z';

  const pathDown = 'M 0 4 C 15 8, 30 22, 45 16 C 60 10, 75 26, 90 28 L 105 22 L 120 34';
  const areaPathDown = 'M 0 4 C 15 8, 30 22, 45 16 C 60 10, 75 26, 90 28 L 105 22 L 120 34 L 120 36 L 0 36 Z';

  const linePath = isUp ? pathUp : pathDown;
  const areaPath = isUp ? areaPathUp : areaPathDown;
  const strokeColor = isHero ? '#ffffff' : isUp ? colorHex : '#ef4444';
  const endY = isUp ? 2 : 34;

  const gradientId = `sparkline-grad-${Math.random().toString(36).substring(2, 7)}`;

  return (
    <div className="w-28 h-9 shrink-0 relative overflow-visible">
      <svg className="w-full h-full overflow-visible" viewBox="0 0 120 36" fill="none">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity={isHero ? '0.4' : '0.25'} />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Soft Area Gradient Fill */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Enterprise Bezier Trend Spline */}
        <path
          d={linePath}
          stroke={strokeColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Live Endpoint Pulsing Status Dot */}
        <circle cx="120" cy={endY} r="3" fill={strokeColor} />
        <circle cx="120" cy={endY} r="6" fill={strokeColor} opacity="0.3" className="animate-pulse" />
      </svg>
    </div>
  );
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  description,
  source,
  trend,
  trendDirection = 'UP',
  icon: Icon,
  variant = 'default',
  className,
}) => {
  const isHero = variant === 'heroEmerald';
  const isUp = trendDirection === 'UP';

  if (isHero) {
    return (
      <Card className={cn('transition-all hover:shadow-xl border border-white/20 bg-gradient-to-br from-[#04050b] via-[#3d5ad6] to-[#4767f0] text-primary-foreground rounded-2xl overflow-hidden relative group', className)}>
        <CardContent className="p-5 space-y-4 relative z-10">
          <div className="flex items-center justify-between">
            <span className="font-heading text-xs font-extrabold uppercase tracking-wider text-blue-100">
              {title}
            </span>
            <div className="rounded-full p-2.5 bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all shadow-inner group-hover:scale-110">
              <ArrowUpRight className="h-5 w-5" />
            </div>
          </div>

          <div className="flex items-end justify-between gap-3">
            <span className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white block drop-shadow-xs">
              {value}
            </span>
            {/* Enterprise Micro Sparkline */}
            <MicroSparkline isUp={isUp} isHero={true} />
          </div>

          <div className="flex items-center justify-between border-t border-white/20 pt-3 text-xs">
            {trend && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 backdrop-blur-md text-white px-2.5 py-0.5 text-[11px] font-extrabold shadow-2xs">
                <TrendingUp className="h-3.5 w-3.5" /> ↑ {trend}
              </span>
            )}
            {source && <SourceBadge source={source} showIcon={false} className="bg-white/20 text-white py-0 px-1.5 text-[9px] font-bold" />}
          </div>
        </CardContent>
      </Card>
    );
  }

  let iconBgClass = 'bg-primary/10 text-primary';
  let borderClass = 'border-border';
  let brandHex = '#3d5ad6';

  if (variant === 'copper') {
    iconBgClass = 'bg-copper/15 text-copper-dark dark:text-amber-300';
    borderClass = 'border-copper/30';
    brandHex = '#4767f0';
  } else if (variant === 'primary') {
    iconBgClass = 'bg-primary/15 text-primary';
    borderClass = 'border-primary/30';
    brandHex = '#3d5ad6';
  }

  return (
    <Card className={cn('transition-all hover:shadow-lg border bg-surface dark:bg-[#0e101f] rounded-2xl overflow-hidden relative group', borderClass, className)}>
      <CardContent className="p-5 space-y-3 relative z-10">
        <div className="flex items-center justify-between">
          <span className="font-heading text-xs font-extrabold uppercase tracking-wider text-foreground-secondary">
            {title}
          </span>
          <div className={cn('rounded-xl p-2.5 shadow-2xs group-hover:scale-105 transition-transform', iconBgClass)}>
            <Icon className="h-5 w-5" />
          </div>
        </div>

        <div className="flex items-end justify-between gap-2">
          <span className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
            {value}
          </span>
          {/* Enterprise Micro Sparkline */}
          <MicroSparkline isUp={isUp} colorHex={brandHex} />
        </div>

        <div className="flex items-center justify-between border-t border-border/60 pt-2.5 text-xs">
          {trend ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold border shadow-2xs shrink-0',
                isUp
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
              )}
            >
              {isUp ? '↑ ' : '↓ '}
              {trend}
            </span>
          ) : (
            <span className="text-[11px] text-foreground-muted font-semibold">{description || 'Current period overview'}</span>
          )}

          {source && <SourceBadge source={source} showIcon={false} className="py-0 px-1.5 text-[9px] font-bold" />}
        </div>
      </CardContent>
    </Card>
  );
};
